import { BarChartOutlined, CloudDownloadOutlined, UploadOutlined } from '@ant-design/icons';
import React, { useState } from 'react';
import { Alert, Badge, Button, Checkbox, Collapse, Modal, Row, Spin, Upload, UploadProps } from 'antd';
import type { UploadFile } from 'antd/es/upload/interface';
import { CustomerServiceQueries } from '../../queries/customer-service-queries';
import { UserImportResult } from '../../models/user-import-result-interface';

const { Panel } = Collapse;

var template = require('../../assets/files/UserImportTemplate.xlsx');

interface ImportUsersModalProps {
  companyId: number;
  isVisible: boolean;
  handleClose(): void;
}

export default function ImportUsersModal({
  companyId,
  isVisible,
  handleClose,
}: ImportUsersModalProps) {
  const [errorAlertVisible, setErrorAlertVisible] = useState<boolean>(false);
  const [errorMsg, setErrorMsg] = useState<string>();
  const [file, setFile] = useState<UploadFile | undefined>();
  const defaultAcceptTypes = '.xls, .xlsx';  
  const [markImportAsActive, setMarkImportAsActive] = useState<boolean>(false);
  const importUsersMutation = CustomerServiceQueries.useImportUsers(companyId, markImportAsActive);
  const [results, setResults] = useState<UserImportResult | undefined>();

  const handleChange: UploadProps['onChange'] = (info) => {
    if (info.file.status === 'done') {
      setFile(info.file);
    }
  };

  const handleImportUsers = async (file: UploadFile) => {
    const formData = new FormData();
    formData.append('file', file as any);

    await importUsersMutation.mutateAsync(formData, {
      onSuccess: (data) => {
        setFile(undefined);
        setResults(data);
      },
    });
  };

  const props = {
    accept: defaultAcceptTypes,
    onChange: handleChange,
    multiple: false,
    beforeUpload: async (file: UploadFile) => {
      setErrorAlertVisible(false);
      setErrorMsg('');
      if (validateFileSize(file) && validateFileType(file)) setFile(file);

      return false;
    },
    onRemove: () => {
      setFile(undefined);
    },
    fileList: !!file ? [file] : [],
  };

  /**
   * Validate file size. Default is 1 GB
   * @param file
   * @returns true if less than max file size, otherwise returns false
   */
  const validateFileSize = (file: UploadFile) => {
    const isLessThanMaxFileSize = file.size! / 1024 / 1024 / 1024 < 1;
    if (!isLessThanMaxFileSize) {
      setErrorMsg(
        `File must be less than '1GB'. Please choose a smaller file.`
      );
      setErrorAlertVisible(true);
    }
    return isLessThanMaxFileSize;
  };

  /**
   * Validate upload file type
   * @param file
   * @returns
   */
  const validateFileType = (file: UploadFile) => {
    let isValidFileType = false;

    if (
      defaultAcceptTypes
        .toLowerCase()
        .split(',')
        .some((d) => file.name.toLowerCase().includes(d))
    )
      isValidFileType = true;

    if (!isValidFileType) {
      setErrorMsg(`File must be of type: ${defaultAcceptTypes}`);
      setErrorAlertVisible(true);
    }
    return isValidFileType;
  };

  const handleMarkImportAsActiveChange = (checked: boolean) => {
    setMarkImportAsActive(checked);
  }

  return (
    <Modal
      bodyStyle={{ overflowY: 'scroll', maxHeight: 'calc(100vh - 200px)' }}
      centered
      title='Import Users'
      visible={isVisible}
      onCancel={() => {
        setFile(undefined);
        setErrorMsg('');
        setErrorAlertVisible(false);
        setResults(undefined);
        handleClose();
      }}
      footer={
        !!!results
          ? [
              <Button
                id='importUsersButton'
                type='primary'
                key='importUsers'
                disabled={!file}
                loading={importUsersMutation.isLoading}
                onClick={() => {
                  setErrorAlertVisible(false);
                  setErrorMsg('');
                  !!file && handleImportUsers(file);
                }}
              >
                Import Users
              </Button>,
            ]
          : [
              <Button
                id='importMoreButton'
                type='primary'
                key='importMore'
                onClick={() => {
                  setErrorAlertVisible(false);
                  setErrorMsg('');
                  setFile(undefined);
                  setResults(undefined);
                }}
              >
                Import More
              </Button>,
              <Button
                id='doneButton'
                key='done'
                onClick={() => {
                  setErrorAlertVisible(false);
                  setErrorMsg('');
                  setResults(undefined);
                  handleClose();
                }}
              >
                Done
              </Button>,
            ]
      }
    >
      {!!!results ? (
        <>
          {errorAlertVisible ? (
            <Alert
              message={errorMsg}
              type='error'
              showIcon
              closable
              afterClose={() => setErrorAlertVisible(false)}
            />
          ) : null}
          <Spin spinning={importUsersMutation.isLoading}>
            <p>
              Import users from an excel file to this company. All imported
              users will be imported as inactive and will need to be manually
              made active by an administrator on the company.
            </p>
            <p>
              <a href={template} style={{ padding: 0, fontWeight: 600 }}>
                Download Template <CloudDownloadOutlined />
              </a>
              <br />
              <small>
                Note: Do NOT reorder the columns from the template. Additional
                added columns will be ignored.
              </small>
            </p>
            <p>
              <Upload {...props}>
                <Button icon={<UploadOutlined />}>Choose Excel File</Button>
              </Upload>
              <br />
              <small>Accepted file types: .xls, .xlsx</small>
            </p>
            <Row>
              <Checkbox
                checked={markImportAsActive}
                onChange={(e) => handleMarkImportAsActiveChange(e.target.checked)}
              >
                Mark Imported Users As Active
              <br />
              <small>
                (Users will be marked as Active and Notified but will NOT receive the invitation email)
              </small>
              </Checkbox>
            </Row>
          </Spin>
        </>
      ) : (
        <ResultsDisplay results={results} />
      )}
    </Modal>
  );
}

const ResultsDisplay = ({ results }: { results: UserImportResult }) => {
  const duplicateUsers = results?.issues?.filter(
    (i) => i.reason === 'User already exists on company'
  );
  const issueUsers = results?.issues?.filter(
    (i) => i.reason !== 'User already exists on company'
  );
  return (
    <>
      <Row
        style={{
          marginBottom: 16,
          display: 'inline-flex',
          alignItems: 'center',
        }}
      >
        <b>IMPORT RESULTS</b>
        <BarChartOutlined style={{ marginLeft: 5 }} />
      </Row>
      <Collapse ghost bordered={false}>
        <Panel
          style={{ borderWidth: 0, backgroundColor: 'transparent' }}
          collapsible={!!results?.addedUsers?.length ? 'header' : 'disabled'}
          showArrow={false}
          key='imported'
          header={
            <PanelHeader
              title='Imported Users'
              description='Users that were successfully imported'
              count={results.addedUsers?.length}
            />
          }
        >
          {results.addedUsers?.map((u) => (
            <p style={{ paddingLeft: 24 }} key={u.userId}>
              <b>{u.fullName}</b>
              <br />
              <span>{u.emailAddress}</span>
            </p>
          ))}
        </Panel>
        <Panel
          style={{ borderWidth: 0, backgroundColor: 'transparent' }}
          collapsible={!!duplicateUsers?.length ? 'header' : 'disabled'}
          showArrow={false}
          key='duplicate'
          header={
            <PanelHeader
              title='Duplicate Users'
              description='Users that already existed on this company and were skipped'
              count={duplicateUsers?.length}
            />
          }
        >
          {duplicateUsers?.map((u) => (
            <p style={{ paddingLeft: 24 }} key={u.row}>
              <b>{u.email}</b>
              <br />
              <span>Row: {u.row}</span>
            </p>
          ))}
        </Panel>
        <Panel
          style={{ borderWidth: 0, backgroundColor: 'transparent' }}
          collapsible={!!issueUsers?.length ? 'header' : 'disabled'}
          showArrow={false}
          key='issue'
          header={
            <PanelHeader
              title='Issues'
              description='Users that had issues preventing import'
              count={issueUsers?.length}
            />
          }
        >
          {issueUsers?.map((u) => (
            <p style={{ paddingLeft: 24 }} key={u.row}>
              <b>{u.email}</b>
              <br />
              <span>
                <b>Row:</b> {u.row}
              </span>
              <br />
              <span>
                <b>Reason:</b> {u.reason}
              </span>
            </p>
          ))}
        </Panel>
      </Collapse>
    </>
  );
};

const PanelHeader = ({
  title,
  description,
  count,
}: {
  title: string;
  description: string;
  count: number;
}) => {
  return (
    <span>
      <span
        style={{
          display: 'inline-flex',
          alignItems: 'center',
        }}
      >
        <b style={{ color: !!count ? '#1B8080' : '#767878' }}>{title}</b>
        <Badge
          count={count}
          showZero
          style={{ marginLeft: 5, backgroundColor: '#767878' }}
        />
      </span>
      <br />
      <small>{description}</small>
    </span>
  );
};
